import { useCallback } from 'react';
import { useAuthUser } from '../contexts/AuthUserContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useAuth0 } from '../components/auth/DevAuth0Provider';

/**
 * Custom hook for managing customer data access and isolation
 * 
 * This hook provides utilities for:
 * - Getting the effective customer ID for API calls
 * - Checking access permissions for customer data
 * - Making customer-scoped API requests
 * - Handling impersonation scenarios
 */
export const useDataAccess = () => {
  const user = useAuthUser();
  const { 
    currentCustomer, 
    isImpersonating, 
    getEffectiveCustomerId, 
    canAccessCustomer,
    getEffectiveChatEndpoint 
  } = useCustomer();
  const { getAccessTokenSilently } = useAuth0();

  // Get the customer ID to use for API calls
  const getApiCustomerId = useCallback(() => {
    const customerId = getEffectiveCustomerId();

    if (!customerId || customerId === 'unknown') {
      console.warn('No valid customer ID available for API calls');
      return null;
    }

    return customerId;
  }, [getEffectiveCustomerId, currentCustomer, isImpersonating]);

  // Get the chat endpoint for the current customer
  const getChatEndpoint = useCallback(() => {
    const endpoint = getEffectiveChatEndpoint();
    
    // Ensure endpoint starts with /
    if (!endpoint.startsWith('/')) {
      return `/${endpoint}`;
    }
    
    return endpoint;
  }, [getEffectiveChatEndpoint]);

  // Check if current user can access specific customer data
  const validateCustomerAccess = useCallback((targetCustomerId) => {
    if (!targetCustomerId) {
      throw new Error('Customer ID is required');
    }

    if (!canAccessCustomer(targetCustomerId)) {
      throw new Error(`Access denied: Cannot access customer '${targetCustomerId}' data`);
    }

    return true;
  }, [canAccessCustomer]);

  // Create customer-scoped API URL
  const createCustomerApiUrl = useCallback((endpoint, customerId = null) => {
    const targetCustomerId = customerId || getApiCustomerId();

    if (!targetCustomerId) {
      throw new Error('No customer ID available for API call');
    }

    validateCustomerAccess(targetCustomerId);

    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'https://localhost:5000';

    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    return `${baseUrl}/api/customers/${targetCustomerId}/${cleanEndpoint}`;
  }, [getApiCustomerId, validateCustomerAccess, currentCustomer, isImpersonating]);

  // Make authenticated API request with customer scope
  const makeCustomerApiRequest = useCallback(async (endpoint, options = {}, customerId = null) => {
    try {
      // Log the current context for debugging
      // console.log('🔍 makeCustomerApiRequest called with:', {
        // endpoint,
        // customerId,
        // currentCustomer: currentCustomer?.id,
        // isImpersonating,
        // effectiveCustomerId: getEffectiveCustomerId()
      // });

      const url = createCustomerApiUrl(endpoint, customerId);
      // console.log('🔍 API URL created:', url);

      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      const requestOptions = {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      };

      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        // Handle rate limiting specifically
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After') || '60';
          console.warn(`Rate limited. Retry after ${retryAfter} seconds`);
          throw new Error(`Rate limited. Please try again in ${retryAfter} seconds.`);
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Customer API request failed:', error);
      throw error;
    }
  }, [createCustomerApiUrl, getAccessTokenSilently, currentCustomer, isImpersonating, getEffectiveCustomerId]);

  // Make chat API request
  const makeChatApiRequest = useCallback(async (message, history = [], sessionId = null) => {
    try {
      const customerId = getApiCustomerId();
      if (!customerId) {
        throw new Error('No customer ID available for chat');
      }

      const chatEndpoint = getChatEndpoint();
      const baseUrl = process.env.REACT_APP_HELIOS_CHAT_API_BASE || 'https://api.helios-core.com';
      const url = `${baseUrl}${chatEndpoint}`;

      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message,
          history,
          session_id: sessionId || `${customerId}-${Date.now()}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Chat API request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Chat API request failed:', error);
      throw error;
    }
  }, [getApiCustomerId, getChatEndpoint, getAccessTokenSilently]);

  // Get current access context for debugging
  const getAccessContext = useCallback(() => {
    return {
      user: {
        email: user?.email,
        customerId: user?.customerId,
        isHeliosAdmin: user?.isHeliosAdmin,
        roles: user?.roles
      },
      customer: {
        current: currentCustomer,
        isImpersonating,
        effectiveId: getEffectiveCustomerId(),
        chatEndpoint: getEffectiveChatEndpoint()
      },
      permissions: {
        canAccessAllCustomers: user?.isHeliosAdmin || false,
        canImpersonate: user?.isHeliosAdmin || false
      }
    };
  }, [user, currentCustomer, isImpersonating, getEffectiveCustomerId, getEffectiveChatEndpoint]);

  // Utility to log access for audit purposes
  const logAccess = useCallback((action, targetCustomerId = null, additionalData = {}) => {
    const context = getAccessContext();
    const logData = {
      timestamp: new Date().toISOString(),
      action,
      targetCustomerId: targetCustomerId || getApiCustomerId(),
      user: context.user.email,
      isImpersonating: context.customer.isImpersonating,
      ...additionalData
    };

    // In production, this should go to a proper audit log
    // console.log('AUDIT:', logData);
    
    return logData;
  }, [getAccessContext, getApiCustomerId]);

  return {
    // Customer identification
    getApiCustomerId,
    getChatEndpoint,
    
    // Access control
    validateCustomerAccess,
    canAccessCustomer,
    
    // API utilities
    createCustomerApiUrl,
    makeCustomerApiRequest,
    makeChatApiRequest,
    
    // Context and debugging
    getAccessContext,
    logAccess,
    
    // Current state
    currentCustomer,
    isImpersonating,
    isHeliosAdmin: user?.isHeliosAdmin || false
  };
};

export default useDataAccess;
